"use client";

import { useEffect, useSyncExternalStore } from "react";
import type { CharState, BiSEntry, EquippedMythState, GroupID, ContentFilter, LootCache, RecommendationMode } from "./types";
import { CLASSES, CLASS_BY_ID } from "./classes";
import { API_BASE } from "./api";
import { onSignIn, type AuthUser } from "./auth";
import { deriveEquippedMyth, type EquippedItem } from "./myth";
import { track } from "./analytics";

const STORAGE_KEY = "bonusroll:state:v1";

const DEFAULT_STATE: CharState = {
  classID: 11, // Druid
  playedSpecID: 105, // Restoration Druid
  selectedCharacterID: null,
  bis: {},
  exclusions: {},
  contentFilter: "both",
  recommendationMode: "upgrade",
  equippedMyth: {
    satisfiedGroups: {},
    mythItemIDs: [],
  },
};

let memState: CharState = DEFAULT_STATE;
let hydrated = false;
let isSignedIn = false;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function readStorage(): CharState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CharState>;
    return { ...DEFAULT_STATE, ...parsed,
      equippedMyth: { ...DEFAULT_STATE.equippedMyth, ...(parsed.equippedMyth ?? {}) },
    };
  } catch {
    return null;
  }
}

function writeStorage(s: CharState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch { /* quota / private mode — silently ignore */ }
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): CharState { return memState; }
function getServerSnapshot(): CharState { return DEFAULT_STATE; }

// ── Server sync ──────────────────────────────────────────────────────────
//
// When the user is signed in, we mirror memState to /api/state so it
// follows them across browsers. Local-first: every mutation hits
// localStorage immediately, and a debounced PUT flushes to the server
// 750ms after the last change (so dragging a slider doesn't spam the API).

const SYNC_DEBOUNCE_MS = 750;

async function pullFromServer(): Promise<void> {
  try {
    const resp = await fetch(`${API_BASE}/api/state`, { credentials: "include" });
    if (resp.status === 404) {
      // No remote state yet — push our local snapshot up.
      await pushToServer();
      return;
    }
    if (!resp.ok) return;
    const remote = (await resp.json()) as Partial<CharState>;
    // Last-writer-wins via updatedAt. If local has been edited more
    // recently than remote, push local up instead of clobbering it
    // with stale remote data — fixes the "edit + refresh within 750ms
    // = lost edit" race.
    const remoteAt = remote.updatedAt ?? 0;
    const localAt = memState.updatedAt ?? 0;
    if (localAt > remoteAt) {
      await pushToServer();
      return;
    }
    memState = {
      ...DEFAULT_STATE,
      ...remote,
      equippedMyth: { ...DEFAULT_STATE.equippedMyth, ...(remote.equippedMyth ?? {}) },
    };
    writeStorage(memState);
    emit();
  } catch { /* offline — local stays */ }
}

async function pushToServer(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/state`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(memState),
    });
  } catch { /* swallow — local is still source of truth */ }
}

function scheduleSync() {
  if (!isSignedIn) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void pushToServer();
  }, SYNC_DEBOUNCE_MS);
}

/** Synchronously fire the pending PUT on tab close. fetch with
 *  keepalive: true tells the browser to keep the request alive
 *  through the page unload — the standard replacement for the
 *  flaky beforeunload pattern. Same CORS rules as a normal fetch
 *  (Access-Control-Allow-Credentials etc.) so the cookie still
 *  travels along. */
function flushPendingSync() {
  if (!isSignedIn || !pushTimer) return;
  clearTimeout(pushTimer);
  pushTimer = null;
  try {
    void fetch(`${API_BASE}/api/state`, {
      method: "PUT",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(memState),
    });
  } catch { /* nothing we can do at unload time */ }
}

// Wire up auth → sync. Module-level subscribe; cleanup unnecessary.
if (typeof window !== "undefined") {
  onSignIn((_user: AuthUser) => {
    isSignedIn = true;
    void pullFromServer();
  });
  // pagehide is the modern, reliable "tab is going away" event.
  // visibilitychange covers mobile-Safari edge cases where pagehide
  // doesn't fire on backgrounding.
  window.addEventListener("pagehide", flushPendingSync);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPendingSync();
  });
}

/** Read the entire char state. Subscribed components re-render on update. */
export function useCharState(): CharState {
  const s = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    if (hydrated) return;
    hydrated = true;
    const fromDisk = readStorage();
    if (fromDisk) {
      memState = fromDisk;
      emit();
    }
  }, []);

  return s;
}

function update(producer: (s: CharState) => CharState) {
  // Stamp updatedAt on every mutation so pullFromServer can tell
  // "we have unsynced local edits" from "remote moved on without us".
  memState = { ...producer(memState), updatedAt: Date.now() };
  writeStorage(memState);
  emit();
  scheduleSync();
}

// ── Mutators ──────────────────────────────────────────────────────────────

export function setClass(classID: number) {
  const cls = CLASS_BY_ID[classID] ?? CLASSES[0];
  track("class_picked", { classID, className: cls.name });
  update((s) => ({
    ...s,
    classID,
    playedSpecID: cls.specs[0]?.id ?? s.playedSpecID,
    // Deliberately DO NOT wipe `bis` or `exclusions`. `bis` is keyed
    // by specID and `exclusions` by poolID — switching class never
    // collides with another class's data, so previous classes' BiS
    // configs are preserved untouched. (Earlier versions cleared
    // both, which silently nuked the user's BiS the first time the
    // dashboard auto-picked a character whose class didn't match
    // their landing-page choice.)
    //
    // equippedMyth IS class-specific (it's derived from a single
    // character's live equipment), so we DO clear it. The dashboard's
    // equipment-bootstrap effect will repopulate it once a character
    // is selected for the new class.
    equippedMyth: { satisfiedGroups: {}, mythItemIDs: [] },
    // Switching class manually clears the character binding — the user
    // is now in "spec preview" mode rather than acting on a specific
    // character. Selecting a character on the dashboard restores it.
    selectedCharacterID: null,
  }));
}

/** Bind the dashboard / engine to a specific Battle.net character.
 *  Set to null to clear (returns to "spec preview" mode). */
export function setSelectedCharacter(id: number | null) {
  update((s) => ({ ...s, selectedCharacterID: id }));
}

export function setPlayedSpec(specID: number) {
  track("spec_picked", { specID });
  update((s) => ({ ...s, playedSpecID: specID }));
}

export function setContentFilter(f: ContentFilter) {
  track("content_filter_changed", { filter: f });
  update((s) => ({ ...s, contentFilter: f }));
}

export function setRecommendationMode(m: RecommendationMode) {
  track("recommendation_mode_changed", { mode: m });
  update((s) => ({ ...s, recommendationMode: m }));
}

export function setBiS(groupID: GroupID, entry: BiSEntry | null) {
  track("bis_set", {
    groupID,
    mode: entry?.mode ?? "skip",
    itemCount: entry?.mode === "specific" ? (entry.itemIDs?.length ?? 0) : 0,
  });
  update((s) => {
    const specID = s.playedSpecID;
    const cur = { ...(s.bis[specID] ?? {}) };
    if (!entry || entry.mode === "skip") {
      delete cur[groupID];
    } else {
      cur[groupID] = entry.mode === "specific"
        ? { mode: "specific", itemIDs: entry.itemIDs ?? [] }
        : entry;
    }
    return { ...s, bis: { ...s.bis, [specID]: cur } };
  });
}

export function addBiSItem(groupID: GroupID, itemID: number) {
  update((s) => {
    const specID = s.playedSpecID;
    const cur = { ...(s.bis[specID] ?? {}) };
    const e = cur[groupID];
    if (!e || e.mode !== "specific") {
      cur[groupID] = { mode: "specific", itemIDs: [itemID] };
    } else {
      const next = e.itemIDs ?? [];
      if (!next.includes(itemID)) cur[groupID] = { mode: "specific", itemIDs: [...next, itemID] };
    }
    return { ...s, bis: { ...s.bis, [specID]: cur } };
  });
}

export function removeBiSItem(groupID: GroupID, itemID: number) {
  update((s) => {
    const specID = s.playedSpecID;
    const cur = { ...(s.bis[specID] ?? {}) };
    const e = cur[groupID];
    if (!e || e.mode !== "specific") return s;
    const next = (e.itemIDs ?? []).filter((id) => id !== itemID);
    cur[groupID] = { mode: "specific", itemIDs: next };
    return { ...s, bis: { ...s.bis, [specID]: cur } };
  });
}

export function setExcluded(poolID: string, itemID: number, on: boolean) {
  update((s) => {
    const ex = { ...s.exclusions };
    const row = { ...(ex[poolID] ?? {}) };
    if (on) row[itemID] = true; else delete row[itemID];
    if (Object.keys(row).length === 0) delete ex[poolID];
    else ex[poolID] = row;
    return { ...s, exclusions: ex };
  });
}

export function toggleExcluded(poolID: string, itemID: number) {
  update((s) => {
    const cur = !!s.exclusions[poolID]?.[itemID];
    track("item_marked_rolled", { poolID, itemID, becameRolled: !cur });
    const ex = { ...s.exclusions };
    const row = { ...(ex[poolID] ?? {}) };
    if (cur) delete row[itemID]; else row[itemID] = true;
    if (Object.keys(row).length === 0) delete ex[poolID];
    else ex[poolID] = row;
    return { ...s, exclusions: ex };
  });
}

export function setGroupSatisfied(groupID: GroupID, on: boolean) {
  update((s) => {
    const sat = { ...s.equippedMyth.satisfiedGroups };
    if (on) sat[groupID] = true; else delete sat[groupID];
    return { ...s, equippedMyth: { ...s.equippedMyth, satisfiedGroups: sat } };
  });
}

/** Replace the entire equippedMyth state. Used by the dashboard right
 *  after fetching a character's live equipment from Blizzard. */
export function setEquippedMyth(myth: EquippedMythState) {
  update((s) => ({ ...s, equippedMyth: myth }));
}

/** Derive equippedMyth from a character's equipment + the loot cache
 *  (which defines the Myth-track ilvl floor) and store it. The engine
 *  will then automatically:
 *    - skip catalyzed BiS slots whose group is satisfied
 *    - keep specific BiS items as recommendations UNLESS the player
 *      already has that exact item equipped on Myth
 *  This matches the user's spec: "don't recommend already-Myth slots
 *  EXCEPT for BiS, but still skip a BiS recommendation when the player
 *  already owns that specific BiS item." */
export function applyEquipmentToMyth(equipped: EquippedItem[], cache: LootCache) {
  const next = deriveEquippedMyth(equipped, cache);
  setEquippedMyth(next);
}

export function resetCharData() {
  update((s) => ({
    ...s,
    bis: {},
    exclusions: {},
    equippedMyth: { satisfiedGroups: {}, mythItemIDs: [] },
  }));
}
